// @flow

import React from "react";

import Section from "../../components/Section";
import EditableForm from "../../components/EditableForm";
import FormRow from "../../components/FormRow";
import EditableField from "../../components/EditableField";

import {
  PURPOSE_KIND,
  FINANCING_PRODUCT,
  FINANCING_DESCRIPTION,
  INVESTMENT_AMOUNT,
  FINANCING_AMOUNT,
  FINANCING_PRODUCT__VENDOR_LOAN,
  FINANCING_PRODUCT__RELATIVE_LOAN,
  FINANCING_PRODUCT__LOAN,
  FINANCING_PRODUCT__OVERDRAFT,
  FINANCING_PRODUCT__GUARANTEED_CREDIT,
  FINANCING_PRODUCT__HOUSE_BANK_LOAN,
  FINANCING_PRODUCT__PROMOTIONAL_LOAN,
  FINANCING_PRODUCT__KFW_ERP_FGN_LOAN,
  FINANCING_PRODUCT__EQUITY_FINANCING,
  FINANCING_PRODUCT__MICRO_MEZZANINE,
  FINANCING_PRODUCT__OTHER,
  FINANCING_PRODUCT__SILENT_PARTNERSHIP,
  FINANCING_PRODUCT__SUBSIDY,
  FINANCING_PRODUCT__LEASING,
  FINANCING_PRODUCT__EQUITY,
  FINANCING_ASSETS_IN_KIND,
  FINANCING_PRODUCT__OWN_CONTRIBUTION,
  FINANCING_PRODUCT__ONGOING_REVENUE_CASHFLOW
} from "./formFields";
import * as formatters from "../../utils/formatters";
import { getKind, mapPurposeKind, mapSourceOfFundsKeys } from "./mappers";
import SectionTitle from "../../components/Section/SectionTitle";
import styles from "../../components/EditableField/EditableField.scss";

type OkpFinancingInfoProps = {
  initialValues: {
    [PURPOSE_KIND]: string,
    [FINANCING_PRODUCT]: string,
    [FINANCING_DESCRIPTION]: string,
    [FINANCING_AMOUNT]: number,
    [INVESTMENT_AMOUNT]: number
  },
  numberedWithDot?: boolean
};

const PRODUCTS_WITH_DETAILS = [
  FINANCING_PRODUCT__VENDOR_LOAN,
  FINANCING_PRODUCT__RELATIVE_LOAN,
  FINANCING_PRODUCT__LOAN,
  FINANCING_PRODUCT__OVERDRAFT,
  FINANCING_PRODUCT__GUARANTEED_CREDIT,
  FINANCING_PRODUCT__HOUSE_BANK_LOAN,
  FINANCING_PRODUCT__PROMOTIONAL_LOAN,
  FINANCING_PRODUCT__KFW_ERP_FGN_LOAN,
  FINANCING_PRODUCT__EQUITY_FINANCING,
  FINANCING_PRODUCT__MICRO_MEZZANINE,
  FINANCING_PRODUCT__OTHER,
  FINANCING_PRODUCT__SILENT_PARTNERSHIP,
  FINANCING_PRODUCT__SUBSIDY,
  FINANCING_PRODUCT__LEASING
];

const PRODUCTS_WITH_BORROWER_ONLY = [
  FINANCING_PRODUCT__EQUITY,
  FINANCING_ASSETS_IN_KIND,
  FINANCING_PRODUCT__OWN_CONTRIBUTION,
  FINANCING_PRODUCT__ONGOING_REVENUE_CASHFLOW
];

const OkpFinancingInfo = ({ initialValues, numberedWithDot }: OkpFinancingInfoProps) => (
  <Section title="Finanzierungsstruktur" reducedMargin numberedWithDot={numberedWithDot}>
    <EditableForm initialValues={initialValues} isLocked>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: FINANCING_DESCRIPTION,
            caption: () => "Beschreibung"
          }}
        />
        <EditableField
          fieldControlProps={{
            name: INVESTMENT_AMOUNT,
            caption: () => "Investitionssumme"
          }}
          displayValue={formatters.formatDecimalCurrency}
        />
        <EditableField
          fieldControlProps={{
            name: FINANCING_AMOUNT,
            caption: () => "Finanzierungssumme"
          }}
          displayValue={formatters.formatDecimalCurrency}
        />
      </FormRow>

      <SectionTitle>Mittelverwendung</SectionTitle>
      {initialValues[PURPOSE_KIND] ? (
        initialValues[PURPOSE_KIND].map(({ kind }) => (
          <FormRow separator key={kind}>
            <EditableField
              fieldControlProps={{
                name: PURPOSE_KIND,
                caption: () => "Objekt"
              }}
              displayValue={value => {
                const purposeKind = getKind(value, kind);
                return purposeKind && mapPurposeKind(purposeKind.kind);
              }}
            />

            <EditableField
              fieldControlProps={{
                name: PURPOSE_KIND,
                caption: () => "Betrag"
              }}
              displayValue={value => {
                const purposeKind = getKind(value, kind);
                return purposeKind && formatters.formatDecimalCurrency(purposeKind.value);
              }}
            />

            <div />
          </FormRow>
        ))
      ) : (
        <FormRow separator>
          <span className={styles.dumbValue}>-</span>
        </FormRow>
      )}

      <SectionTitle>Mittelherkunft</SectionTitle>
      {initialValues[FINANCING_PRODUCT] ? (
        initialValues[FINANCING_PRODUCT].map(
          ({
            kind,
            borrowerName,
            nonrepaymentMonths,
            indicationInterest,
            guaranteeRatio,
            durationInMonths,
            receivedPreliminaryFunding,
            value
          }) => {
            const shouldDisplayDetail = PRODUCTS_WITH_DETAILS.includes(kind);
            const shouldDisplayBorrowerOnly = PRODUCTS_WITH_BORROWER_ONLY.includes(kind);

            return (
              <>
                <FormRow>
                  <EditableField
                    fieldControlProps={{
                      name: FINANCING_PRODUCT,
                      caption: () => "Objekt"
                    }}
                    displayValue={() => mapSourceOfFundsKeys(kind)}
                  />

                  <EditableField
                    fieldControlProps={{
                      name: FINANCING_PRODUCT,
                      caption: () => "Betrag"
                    }}
                    displayValue={() => formatters.formatDecimalCurrency(value)}
                  />

                  {shouldDisplayDetail && (
                    <EditableField
                      fieldControlProps={{
                        name: FINANCING_PRODUCT,
                        caption: () => "Finanzmittelnehmer oder -geber"
                      }}
                      displayValue={() => borrowerName || "-"}
                    />
                  )}
                  {shouldDisplayBorrowerOnly && (
                    <EditableField
                      fieldControlProps={{
                        name: FINANCING_PRODUCT,
                        caption: () => "Finanzmittelgeber"
                      }}
                      displayValue={() => borrowerName || "-"}
                    />
                  )}
                </FormRow>

                {shouldDisplayDetail && (
                  <FormRow>
                    {kind === FINANCING_PRODUCT__KFW_ERP_FGN_LOAN ? (
                      <EditableField
                        fieldControlProps={{
                          name: FINANCING_PRODUCT,
                          caption: () => "Erhaltene Vorförderungen"
                        }}
                        displayValue={() =>
                          receivedPreliminaryFunding === null || receivedPreliminaryFunding === undefined || receivedPreliminaryFunding === ""
                            ? "-"
                            : formatters.formatDecimalCurrency(receivedPreliminaryFunding)
                        }
                      />
                    ) : (
                      <EditableField
                        fieldControlProps={{
                          name: FINANCING_PRODUCT,
                          caption: () => "Bürgschaftsquote"
                        }}
                        displayValue={() => formatters.formatPercentSafe(guaranteeRatio)}
                      />
                    )}

                    <EditableField
                      fieldControlProps={{
                        name: FINANCING_PRODUCT,
                        caption: () => "Geplante Laufzeit in Monaten"
                      }}
                      displayValue={() => durationInMonths || "-"}
                    />

                    <EditableField
                      fieldControlProps={{
                        name: FINANCING_PRODUCT,
                        caption: () => "Freimonate"
                      }}
                      displayValue={() => nonrepaymentMonths || "-"}
                    />
                  </FormRow>
                )}

                <FormRow separator>
                  {shouldDisplayDetail && (
                    <EditableField
                      fieldControlProps={{
                        name: FINANCING_PRODUCT,
                        caption: () => "Zinssatz (Indikation)"
                      }}
                      displayValue={() => formatters.formatPercentSafe(indicationInterest)}
                    />
                  )}

                  <div />

                  <div />
                </FormRow>
              </>
            );
          }
        )
      ) : (
        <span className={styles.dumbValue}>-</span>
      )}
    </EditableForm>
  </Section>
);

OkpFinancingInfo.defaultProps = {
  numberedWithDot: false
};

export default OkpFinancingInfo;
